import { Navigate } from "react-router-dom";
import Icon from "@ant-design/icons/lib/components/Icon";

import { MFE } from "@/app/routing/mfe";
import AllBotsIcon from "@/shared/assets/icons/all-bots.svg?react";
import CEXBotIcon from "@/shared/assets/icons/cex.svg?react";
import DEXEVMBotIcon from "@/shared/assets/icons/dex-e.svg?react";
import DEXNEBotIcon from "@/shared/assets/icons/dex-ne.svg?react";
import ManualArbIcon from "@/shared/assets/icons/manual-arbitrage.svg?react";
import MultiGridIcon from "@/shared/assets/icons/multi-grid.svg?react";
import TransactionsIcon from "@/shared/assets/icons/transactions.svg?react";
import TVChartsIcon from "@/shared/assets/icons/tv-charts.svg?react";
import VWAP0Icon from "@/shared/assets/icons/vwap0.svg?react";
import VWAP1Icon from "@/shared/assets/icons/vwap1.svg?react";
import {
	CEXAbilities,
	DEXAbilities,
	DEXNEAbility,
	DEXV2Ability,
	MainPagesAbilities,
	MarketPagesAbilities,
	PartyPagesAbilities,
} from "@/shared/constants/abilities";
import { mfeUrl, enableStories } from "@/shared/constants/environments";
import {
	CEXRoutes,
	DEXNERoutes,
	DEXRoutes,
	DEXV2Routes,
	MainRoutes,
	PartyRoutes,
	StoryRoutes,
	getBotRoutePath,
	MarketRoutes,
	MarketAlgosRoutes,
} from "@/shared/routing";
import { UIFlatIcon } from "@/shared/ui/ui-flat-icon";
import { lazily } from "@/shared/utils/lazily";

import { MARKET_PATH } from "../constants/routes";
import {
	PartyCrumbTitle,
	BotCrumbTitle,
	AlgosCrumbTitle,
	MarketCrumbTitle,
} from "../template/header/breadcrumbs/crumb-titles";

import { LoginRedirect } from "./login-redirect";
import { IRouteConfig } from "./types";
import {
	getBotMenuItem,
	getPartyMenuItem,
	getDefaultCrumbConfig,
	getMarketMenuItem,
	getMarketAlgosMenuItem,
} from "./utils";

// MAIN PAGES

// const { AllBots } = lazily(() => import("@/pages/main-all-bots"));
const { PartiesList } = lazily(() => import("@/pages/main-parties-list"));
// const { MarketCheckPartiesList } = lazily(() => import("@/pages/main-market-check"));
const { Access } = lazily(() => import("@/pages/main-access"));
const { Teams } = lazily(() => import("@/pages/main-teams"));
const { ExchangeAccounting } = lazily(() => import("@/pages/main-accounting"));
const { DEXBotCreating } = lazily(() => import("@/pages/main-dex-bot-creating"));
const { CEXBotCreating } = lazily(() => import("@/pages/main-cex-bot-creating"));
const { DEXNEBotCreating } = lazily(() => import("@/pages/main-dex-ne-bot-creating"));
const { Stories } = lazily(() => import("@/pages/stories"));
const { ClientsWithdrawal } = lazily(() => import("@/pages/main-clients-withdrawal"));
const { Omnidata } = lazily(() => import("src/pages/main-omnidata"));

// const { ErrorPage } = lazily(() => import("@/pages/error-page"));

// AUTH

const { Login, RedirectLogin } = lazily(() => import("@/pages/login"));

// PARTY PAGES

// const { MarketCheckBotsList } = lazily(() => import("@/pages/party-market-check"));
const { Markets } = lazily(() => import("@/pages/party-markets"));
// const { Dashboard } = lazily(() => import("@/pages/party-dashboard"));
// const { Multigrid } = lazily(() => import("@/pages/party-multigrid"));
// const { Funding } = lazily(() => import("@/pages/party-funding"));
// const { Modules } = lazily(() => import("@/pages/party-modules"));
// const { Analytics } = lazily(() => import("@/pages/party-candle-analytics"));
// const { Strategies } = lazily(() => import("@/pages/party-strategies"));
// const { ManualArbitrage } = lazily(() => import("@/pages/party-arbitrage"));
const { CEXLauncher } = lazily(() => import("@/pages/party-cex-launcher"));
const { CEXBotExchange } = lazily(() => import("@/pages/cex-bot-exchange"));
const { PartyAccounting } = lazily(() => import("@/pages/party-accounting"));
const { AlgoArbitrage } = lazily(() => import("@/pages/party-cex-cex-arbitrage"));
const { AddressBook } = lazily(() => import("@/pages/party-address-book"));
const { Withdrawal } = lazily(() => import("@/pages/party-withdrawal"));
const { PositionManagement } = lazily(() => import("@/pages/party-position-management"));

// BOTS PAGES

const { CEXBot } = lazily(() => import("@/app/pages-configuration/cex-bot"));
const { CEXBotFloatGrid } = lazily(() => import("@/pages/cex-bot-float-grid"));
// const { DEXBot } = lazily(() => import("@/app/pages-configuration/dex-bot"));
// const { DEXEvmBot } = lazily(() => import("@/app/pages-configuration/dex-evm-bot"));
// const { DEXNEBot } = lazily(() => import("@/app/pages-configuration/dex-ne-bot"));

// MARKET ALGOS
const { MarketAlgos } = lazily(() => import("@/pages/market-algos"));
const { MarketAlgosVWAP0 } = lazily(() => import("src/pages/market-algos-vwap0"));
const { MarketAlgosVWAP1 } = lazily(() => import("src/pages/market-algos-vwap1"));

// STORIES PAGES

const {
	FormDemo,
	DateRangePickerDemo,
	TableDemo,
	WidgetWrapperDemo,
	Toasts,
	ModalsDemo,
	DIExampleComponent,
} = lazily(() => import("@/pages/stories"));

export const MAIN_PAGES_ROUTES_SCHEME: IRouteConfig[] = [
	{
		path: MainRoutes.Login,
		element: <RedirectLogin />,
	},
	{
		path: MainRoutes.Root,
		element: <PartiesList />,
		handle: getDefaultCrumbConfig(MainRoutes.Root),
	},
	{
		path: MainRoutes.AllBots,
		// element: <AllBots />,
		element: <MFE url={mfeUrl} />,
		handle: getDefaultCrumbConfig(MainRoutes.AllBots),

		menuItem: {
			title: "All Bots",
			icon: <Icon component={AllBotsIcon} />,
		},
	},
	{
		path: MainRoutes.MarketCheckPage,
		ability: MainPagesAbilities.MarketCheckView,
		// element: <MarketCheckPartiesList />,
		element: <MFE url={mfeUrl} />,
		handle: getDefaultCrumbConfig(MainRoutes.MarketCheckPage),

		menuItem: {
			title: "Market Check",
			icon: <UIFlatIcon name="fi-rr-assept-document" />,
		},
	},
	{
		path: MainRoutes.AccessPage,
		ability: MainPagesAbilities.AccessView,
		element: <Access />,
		handle: getDefaultCrumbConfig(MainRoutes.AccessPage),

		menuItem: {
			title: "Access",
			icon: <UIFlatIcon name="fi-rr-key" />,
		},
	},
	{
		path: MainRoutes.TeamsPage,
		ability: MainPagesAbilities.TeamsView,
		element: <Teams />,
		handle: getDefaultCrumbConfig(MainRoutes.TeamsPage),

		menuItem: {
			title: "Manage Groups",
			icon: <UIFlatIcon name="fi-rr-users-class" />,
		},
	},
	{
		path: MainRoutes.ExchangeAccountingPage,
		ability: MainPagesAbilities.AccountingView,
		element: <ExchangeAccounting />,
		handle: getDefaultCrumbConfig(MainRoutes.ExchangeAccountingPage),

		menuItem: {
			title: "Accounting",
			icon: <UIFlatIcon name="fi-rr-sack-dollar" />,
		},
	},
	{
		path: MainRoutes.ClientsWithdrawal,
		ability: MainPagesAbilities.WithdrawManagerView,
		element: <ClientsWithdrawal />,
		handle: getDefaultCrumbConfig(MainRoutes.ClientsWithdrawal),

		menuItem: {
			title: "Clients Withdrawal",
			icon: <Icon component={TransactionsIcon} />,
		},
	},
	{
		path: MainRoutes.Omnidata,
		ability: MainPagesAbilities.OmnidataView,
		element: <Omnidata />,
		handle: getDefaultCrumbConfig(MainRoutes.Omnidata),
		menuItem: { title: "Omnidata", icon: <UIFlatIcon name="fi-rr-analyse-alt" /> },
	},
	{
		menuKey: "create-bot-root",
		menuItem: {
			title: "Create Bot",
			icon: <UIFlatIcon name="fi-rr-add" />,
			topDivider: true,
		},

		childrenAsSubMenu: true,
		children: [
			{
				path: MainRoutes.CEXCreatingPage,
				ability: MainPagesAbilities.CEXCreatingView,
				element: <CEXBotCreating />,
				handle: getDefaultCrumbConfig(MainRoutes.CEXCreatingPage),

				menuItem: {
					title: "CEX",
					icon: <Icon component={CEXBotIcon} />,
				},
			},
			{
				path: `${MainRoutes.DEXCreatingPage}/:uuid?/`,
				pathKey: MainRoutes.DEXCreatingPage,
				ability: MainPagesAbilities.DEXCreatingView,
				element: <DEXBotCreating />,
				handle: getDefaultCrumbConfig(`${MainRoutes.DEXCreatingPage}/:uuid?/`),

				menuItem: { title: "DEX EVM", icon: <Icon component={DEXEVMBotIcon} /> },
			},
			{
				path: MainRoutes.DEXNECreatingPage,
				ability: MainPagesAbilities.DEXNECreatingView,
				element: <DEXNEBotCreating />,
				handle: getDefaultCrumbConfig(MainRoutes.DEXNECreatingPage),

				menuItem: { title: "DEX NE", icon: <Icon component={DEXNEBotIcon} /> },
			},
		],
	},
	{
		path: MainRoutes.Stories,
		element: <Stories />,
		handle: getDefaultCrumbConfig(MainRoutes.Stories),

		menuItem: {
			title: "Stories",
			icon: <UIFlatIcon name="fi-rr-apps-add" />,
			topDivider: true,
		},

		hide: !enableStories,

		childrenAsSubMenu: true,
		children: [
			{
				menuItem: { title: "Date Picker" },
				path: StoryRoutes.DatePickerStory,
				element: <DateRangePickerDemo />,
				handle: getDefaultCrumbConfig(StoryRoutes.DatePickerStory),
			},
			{
				menuItem: { title: "Form" },
				path: StoryRoutes.Form,
				element: <FormDemo />,
				handle: getDefaultCrumbConfig(StoryRoutes.Form),
			},
			{
				menuItem: { title: "Table" },
				path: StoryRoutes.TableStory,
				element: <TableDemo />,
				handle: getDefaultCrumbConfig(StoryRoutes.TableStory),
			},
			{
				menuItem: { title: "Widget Wrap." },
				path: StoryRoutes.WidgetWrapper,
				element: <WidgetWrapperDemo />,
				handle: getDefaultCrumbConfig(StoryRoutes.WidgetWrapper),
			},
			{
				menuItem: { title: "Toasts" },
				path: StoryRoutes.ToastsStory,
				element: <Toasts />,
				handle: getDefaultCrumbConfig(StoryRoutes.ToastsStory),
			},
			{
				menuItem: { title: "Modals" },
				path: StoryRoutes.Modal,
				element: <ModalsDemo />,
				handle: getDefaultCrumbConfig(StoryRoutes.Modal),
			},
			{
				menuItem: { title: "DI" },
				path: StoryRoutes.DIExample,
				element: <DIExampleComponent />,
				handle: getDefaultCrumbConfig(StoryRoutes.DIExample),
			},
			{
				menuItem: { title: "PNL (old)" },
				path: StoryRoutes.PNLStory,
				element: <MFE url={mfeUrl} />,
				handle: getDefaultCrumbConfig(StoryRoutes.PNLStory),
			},
			{
				menuItem: { title: "Graph (old)" },
				path: StoryRoutes.GraphStory,
				element: <MFE url={mfeUrl} />,
				handle: getDefaultCrumbConfig(StoryRoutes.GraphStory),
			},
		],
	},
];

export const AUTH_ROUTES_SCHEME: IRouteConfig[] = [
	{
		path: MainRoutes.Any,
		element: <LoginRedirect />,
	},
	{
		path: MainRoutes.Root,
		element: <Navigate replace to={MainRoutes.Login} />,
	},
	{
		path: MainRoutes.Login,
		element: <Login />,
	},
];

const MULTIGRID_CONFIG: IRouteConfig = {
	path: PartyRoutes.Multigrid,
	ability: PartyPagesAbilities.MultigridView,
	// element: <Multigrid />,
	element: <MFE url={mfeUrl} />,
	handle: getDefaultCrumbConfig(PartyRoutes.Multigrid),

	menuItem: getPartyMenuItem({
		title: "Multi Grid",
		icon: <Icon component={MultiGridIcon} />,
		section: PartyRoutes.Multigrid,
		indicationName: "multigrid",
	}),
};

export const PARTY_ROUTES_SCHEME: IRouteConfig[] = [
	{
		path: PartyRoutes.Bots,
		// element: <AllBots />,
		element: <MFE url={mfeUrl} />,
		handle: getDefaultCrumbConfig(PartyRoutes.Bots),

		menuItem: getPartyMenuItem({
			title: "Bots",
			icon: <Icon component={AllBotsIcon} />,
			section: PartyRoutes.Bots,
		}),
	},
	{
		path: PartyRoutes.Markets,
		ability: PartyPagesAbilities.MarketView,
		element: <Markets />,
		handle: getDefaultCrumbConfig(PartyRoutes.Markets),

		menuItem: getPartyMenuItem({
			title: "Markets",
			icon: <UIFlatIcon name="fi-rr-shop" />,
			section: PartyRoutes.Markets,
		}),
	},
	{
		path: PartyRoutes.MarketCheck,
		ability: PartyPagesAbilities.MarketCheck,
		// element: <MarketCheckBotsList />,
		element: <MFE url={mfeUrl} />,
		handle: getDefaultCrumbConfig(PartyRoutes.MarketCheck),

		menuItem: getPartyMenuItem({
			title: "Market Check",
			icon: <UIFlatIcon name="fi-rr-assept-document" />,
			section: PartyRoutes.MarketCheck,
		}),
	},
	{
		path: PartyRoutes.Dashboard,
		ability: PartyPagesAbilities.DashboardView,
		// element: <Dashboard />,
		element: <MFE url={mfeUrl} />,
		handle: getDefaultCrumbConfig(PartyRoutes.Dashboard),

		menuItem: getPartyMenuItem({
			title: "Dashboard",
			icon: <UIFlatIcon name="fi-rr-chart-simple" />,
			section: PartyRoutes.Dashboard,
		}),
	},
	MULTIGRID_CONFIG,
	{
		path: PartyRoutes.Funding,
		ability: PartyPagesAbilities.FundingView,
		// element: <Funding />,
		element: <MFE url={mfeUrl} />,
		handle: getDefaultCrumbConfig(PartyRoutes.Funding),

		menuItem: getPartyMenuItem({
			title: "Funding",
			icon: <UIFlatIcon name="fi-rr-dollar" />,
			section: PartyRoutes.Funding,
		}),
	},
	{
		path: PartyRoutes.Modules,
		ability: PartyPagesAbilities.ModulesView,
		// element: <Modules />,
		element: <MFE url={mfeUrl} />,
		handle: getDefaultCrumbConfig(PartyRoutes.Modules),

		menuItem: getPartyMenuItem({
			title: "Modules",
			icon: <UIFlatIcon name="fi-rr-objects-column" />,
			section: PartyRoutes.Modules,
		}),
	},
	{
		path: PartyRoutes.CandlesAnalytic,
		ability: PartyPagesAbilities.CandleAnalyticsView,
		// element: <Analytics />,
		element: <MFE url={mfeUrl} />,
		handle: getDefaultCrumbConfig(PartyRoutes.CandlesAnalytic),

		menuItem: getPartyMenuItem({
			title: "TV Charts",
			icon: <Icon component={TVChartsIcon} />,
			section: PartyRoutes.CandlesAnalytic,
		}),
	},
	{
		path: PartyRoutes.Strategies,
		ability: PartyPagesAbilities.StrategiesView,
		// element: <Strategies />,
		element: <MFE url={mfeUrl} />,
		handle: getDefaultCrumbConfig(PartyRoutes.Strategies),

		menuItem: getPartyMenuItem({
			title: "Strategies",
			icon: <UIFlatIcon name="fi-rr-graduation-cap" />,
			section: PartyRoutes.Strategies,
		}),
	},
	{
		path: PartyRoutes.ManualArbitrage,
		ability: PartyPagesAbilities.ArbitrageView,
		// element: <ManualArbitrage />,
		element: <MFE url={mfeUrl} />,
		handle: getDefaultCrumbConfig(PartyRoutes.ManualArbitrage),

		menuItem: getPartyMenuItem({
			title: "Manual Arbitrage",
			icon: <Icon component={ManualArbIcon} />,
			section: PartyRoutes.ManualArbitrage,
		}),
	},
	{
		path: PartyRoutes.CEXLauncher,
		ability: PartyPagesAbilities.LauncherView,
		element: <CEXLauncher />,
		handle: getDefaultCrumbConfig(PartyRoutes.CEXLauncher),

		menuItem: getPartyMenuItem({
			title: "Launcher",
			icon: <UIFlatIcon name="fi-rr-rocket-lunch" />,
			section: PartyRoutes.CEXLauncher,
		}),
	},
	{
		path: PartyRoutes.CEXCEXArbitrage,
		ability: PartyPagesAbilities.AlgoArbitrageView,
		element: <AlgoArbitrage />,
		handle: getDefaultCrumbConfig(PartyRoutes.CEXCEXArbitrage),

		menuItem: getPartyMenuItem({
			title: "CEX CEX Arbitrage",
			icon: <UIFlatIcon name="fi-rr-coins" />,
			section: PartyRoutes.CEXCEXArbitrage,
		}),
	},
	{
		path: PartyRoutes.Accounting,
		ability: PartyPagesAbilities.DashboardView,
		element: <PartyAccounting />,
		handle: getDefaultCrumbConfig(PartyRoutes.Accounting),

		menuItem: getPartyMenuItem({
			title: "Accounting",
			icon: <UIFlatIcon name="fi-rr-sack-dollar" />,
			section: PartyRoutes.Accounting,
		}),
	},
	{
		path: PartyRoutes.AddressBook,
		ability: PartyPagesAbilities.AddressBookView,
		element: <AddressBook />,
		handle: getDefaultCrumbConfig(PartyRoutes.AddressBook),

		menuItem: getPartyMenuItem({
			title: "Address Book",
			icon: <UIFlatIcon name="fi-rr-notebook-alt" />,
			section: PartyRoutes.AddressBook,
		}),
	},
	{
		path: PartyRoutes.Withdrawals,
		ability: PartyPagesAbilities.WithdrawClientView,
		element: <Withdrawal />,
		handle: getDefaultCrumbConfig(PartyRoutes.Withdrawals),

		menuItem: getPartyMenuItem({
			title: "Withdrawal",
			icon: <Icon component={TransactionsIcon} />,
			section: PartyRoutes.Withdrawals,
		}),
	},
	{
		path: PartyRoutes.PositionManagement,
		ability: PartyPagesAbilities.DashboardView,
		element: <PositionManagement />,
		handle: getDefaultCrumbConfig(PartyRoutes.PositionManagement),

		menuItem: getPartyMenuItem({
			title: "Position Management",
			icon: <UIFlatIcon name="fi-rr-envelope" />,
			section: PartyRoutes.PositionManagement,
		}),
	},
];

const CEX_CONTENT_ROUTES_SCHEME: IRouteConfig[] = [
	{
		path: CEXRoutes.Stats,
		ability: CEXAbilities.StatsView,
		handle: getDefaultCrumbConfig(CEXRoutes.Stats),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Stats",
			icon: <UIFlatIcon name="fi-rr-chart-pie-alt" />,
			botType: "CEX",
			botTab: CEXRoutes.Stats,
		}),
	},
	{
		path: CEXRoutes.Exchange,
		ability: CEXAbilities.ExchangeView,
		handle: getDefaultCrumbConfig(CEXRoutes.Exchange),
		element: <CEXBotExchange />,

		menuItem: getBotMenuItem({
			title: "Exchange",
			icon: <UIFlatIcon name="fi-rr-stats" />,
			botType: "CEX",
			botTab: CEXRoutes.Exchange,
		}),
	},
	{
		path: CEXRoutes.Settings,
		ability: CEXAbilities.SettingsView,
		handle: getDefaultCrumbConfig(CEXRoutes.Settings),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Settings",
			icon: <UIFlatIcon name="fi-rr-settings" />,
			botType: "CEX",
			botTab: CEXRoutes.Settings,
			indicationName: "cexSettings",
		}),
	},
	{
		path: CEXRoutes.Grid,
		ability: CEXAbilities.GridView,
		handle: getDefaultCrumbConfig(CEXRoutes.Grid),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Grid",
			icon: <UIFlatIcon name="fi-rr-grid-alt" />,
			botType: "CEX",
			botTab: CEXRoutes.Grid,
			indicationName: "cexGrid",
		}),
	},
	{
		path: CEXRoutes.APIKeys,
		ability: CEXAbilities.ApiKeysView,
		handle: getDefaultCrumbConfig(CEXRoutes.APIKeys),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "API",
			icon: <UIFlatIcon name="fi-rr-key-skeleton-left-right" />,
			botType: "CEX",
			botTab: CEXRoutes.APIKeys,
		}),
	},
	{
		path: CEXRoutes.FloatGrid,
		ability: CEXAbilities.ExchangeView,
		element: <CEXBotFloatGrid />,
		handle: getDefaultCrumbConfig(CEXRoutes.FloatGrid),

		menuItem: getBotMenuItem({
			title: "Float Grid",
			icon: <UIFlatIcon name="fi-rr-grid-alt" />,
			botType: "CEX",
			botTab: CEXRoutes.FloatGrid,
		}),
	},
	{
		...MULTIGRID_CONFIG,
		menuItem: {
			...MULTIGRID_CONFIG.menuItem,
			topDivider: true,
		},
	},
];

export const CEX_ROUTES_SCHEME: IRouteConfig[] = [
	{
		path: getBotRoutePath({ botType: "CEX" }),
		element: <CEXBot />,
		handle: {
			crumb: {
				title: <BotCrumbTitle />,
				selectMenuType: "cex-bots",
			},
		},

		children: CEX_CONTENT_ROUTES_SCHEME,
	},
];

const DEX_ROUTES_CONTENT_SCHEME: IRouteConfig[] = [
	{
		path: DEXRoutes.Stats,
		ability: DEXAbilities.StatsView,
		handle: getDefaultCrumbConfig(DEXRoutes.Stats),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Stats",
			icon: <UIFlatIcon name="fi-rr-chart-pie-alt" />,
			botType: "DEX",
			botTab: DEXRoutes.Stats,
		}),
	},
	{
		path: DEXRoutes.Settings,
		ability: DEXAbilities.SettingsView,
		handle: getDefaultCrumbConfig(DEXRoutes.Settings),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Settings",
			icon: <UIFlatIcon name="fi-rr-settings" />,
			botType: "DEX",
			botTab: DEXRoutes.Settings,
		}),
	},
	{
		path: DEXRoutes.Funding,
		ability: DEXAbilities.FundingView,
		handle: getDefaultCrumbConfig(DEXRoutes.Funding),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Funding",
			icon: <UIFlatIcon name="fi-rr-dollar" />,
			botType: "DEX",
			botTab: DEXRoutes.Funding,
		}),
	},
];

export const DEX_ROUTES_SCHEME: IRouteConfig[] = [
	{
		path: getBotRoutePath({ botType: "DEX" }),
		// element: <DEXBot />,
		element: <MFE url={mfeUrl} />,
		handle: { crumb: { title: <BotCrumbTitle /> } },

		children: DEX_ROUTES_CONTENT_SCHEME,
	},
];

const DEX_V2_ROUTES_CONTENT_SCHEME: IRouteConfig[] = [
	{
		path: DEXV2Routes.Stats,
		ability: DEXV2Ability.StatsView,
		handle: getDefaultCrumbConfig(DEXV2Routes.Stats),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Stats",
			icon: <UIFlatIcon name="fi-rr-chart-pie-alt" />,
			botType: "DEX_V2",
			botTab: DEXV2Routes.Stats,
		}),
	},
	{
		path: DEXV2Routes.Settings,
		ability: DEXV2Ability.SettingsView,
		handle: getDefaultCrumbConfig(DEXV2Routes.Settings),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Settings",
			icon: <UIFlatIcon name="fi-rr-settings" />,
			botType: "DEX_V2",
			botTab: DEXV2Routes.Settings,
		}),
	},
	{
		path: DEXV2Routes.Swap,
		ability: DEXV2Ability.ExchangeView,
		handle: getDefaultCrumbConfig(DEXV2Routes.Swap),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Swap",
			icon: <UIFlatIcon name="fi-rr-chess-knight-alt" />,
			botType: "DEX_V2",
			botTab: DEXV2Routes.Swap,
		}),
	},
];

export const DEX_V2_ROUTES_SCHEME: IRouteConfig[] = [
	{
		path: getBotRoutePath({ botType: "DEX_V2" }),
		// element: <DEXEvmBot />,
		element: <MFE url={mfeUrl} />,
		handle: { crumb: { title: <BotCrumbTitle /> } },

		children: DEX_V2_ROUTES_CONTENT_SCHEME,
	},
];

const DEX_NE_ROUTES_CONTENT_SCHEME: IRouteConfig[] = [
	{
		path: DEXNERoutes.Stats,
		ability: DEXNEAbility.StatsView,
		handle: getDefaultCrumbConfig(DEXNERoutes.Stats),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Stats",
			icon: <UIFlatIcon name="fi-rr-chart-pie-alt" />,
			botType: "DEX_NE",
			botTab: DEXNERoutes.Stats,
		}),
	},
	{
		path: DEXNERoutes.Settings,
		ability: DEXNEAbility.SettingsView,
		handle: getDefaultCrumbConfig(DEXNERoutes.Settings),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Settings",
			icon: <UIFlatIcon name="fi-rr-settings" />,
			botType: "DEX_NE",
			botTab: DEXNERoutes.Settings,
		}),
	},
	{
		path: DEXNERoutes.Swap,
		ability: DEXNEAbility.ExchangeView,
		handle: getDefaultCrumbConfig(DEXNERoutes.Swap),
		element: <MFE url={mfeUrl} />,

		menuItem: getBotMenuItem({
			title: "Swap",
			icon: <UIFlatIcon name="fi-rr-chess-knight-alt" />,
			botType: "DEX_NE",
			botTab: DEXNERoutes.Swap,
		}),
	},
];

export const DEX_NE_ROUTES_SCHEME: IRouteConfig[] = [
	{
		path: getBotRoutePath({ botType: "DEX_NE" }),
		// element: <DEXNEBot />,
		element: <MFE url={mfeUrl} />,
		handle: { crumb: { title: <BotCrumbTitle /> } },

		children: DEX_NE_ROUTES_CONTENT_SCHEME,
	},
];

export const MARKET_ROUTES_CONTENT_SCHEMA: IRouteConfig[] = [
	{
		path: MarketRoutes.Algos,
		handle: { crumb: { title: <AlgosCrumbTitle /> } },

		menuItem: getMarketMenuItem({
			title: "Algos",
			section: MarketRoutes.Algos,
			icon: <UIFlatIcon name="fi-rr-rectangle-list" />,
		}),

		childrenAsSubMenu: true,
		children: [
			{ path: "", element: <MarketAlgos /> },
			{
				path: MarketAlgosRoutes.VWAP0,
				element: <MarketAlgosVWAP0 />,
				handle: getDefaultCrumbConfig(MarketAlgosRoutes.VWAP0),
				ability: MarketPagesAbilities.VWAP0View,

				menuItem: getMarketAlgosMenuItem({
					title: "VWAP0",
					section: MarketAlgosRoutes.VWAP0,
					icon: <Icon component={VWAP0Icon} />,
				}),
			},
			{
				path: MarketAlgosRoutes.VWAP1,
				element: <MarketAlgosVWAP1 />,
				handle: getDefaultCrumbConfig(MarketAlgosRoutes.VWAP1),
				ability: MarketPagesAbilities.VWAP1View,

				menuItem: getMarketAlgosMenuItem({
					title: "VWAP1",
					section: MarketAlgosRoutes.VWAP1,
					icon: <Icon component={VWAP1Icon} />,
				}),
			},
		],
	},
];

const MARKET_ROUTES_SCHEMA: IRouteConfig[] = [
	{
		path: MARKET_PATH,
		handle: { crumb: { title: <MarketCrumbTitle />, selectMenuType: "markets" } },

		children: MARKET_ROUTES_CONTENT_SCHEMA,
	},
];

const PARTY_GROUP_ROUTES: IRouteConfig[] = [
	{
		path: "party/:partyUUID",
		handle: {
			crumb: {
				title: <PartyCrumbTitle />,
				selectMenuType: "parties",
			},
		},
		children: [
			...PARTY_ROUTES_SCHEME,
			...CEX_ROUTES_SCHEME,
			...DEX_ROUTES_SCHEME,
			...DEX_V2_ROUTES_SCHEME,
			...DEX_NE_ROUTES_SCHEME,
			...MARKET_ROUTES_SCHEMA,
		],
	},
];

export const ROUTES_SCHEME = [...MAIN_PAGES_ROUTES_SCHEME, ...PARTY_GROUP_ROUTES];
